import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Play, RefreshCw, Shuffle, ThumbsDown, ThumbsUp, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { rateTrack } from "@/lib/ratings";
import { usePlaylists } from "@/context/PlaylistsContext";
import { TrackTable } from "@/components/TrackTable";
import { EmptyState } from "@/components/PageHeader";
import { usePlayer } from "@/context/PlayerContext";

/* Songs the user rated thumbs-up (rating 1) or thumbs-down (-1). `ids` (a Set from the ratings store) drives refetch + instant removal. */
export const RatedList = ({ rating, ids }) => {
  const [tracks, setTracks] = useState(null);
  const [making, setMaking] = useState(false);
  const p = usePlayer();
  const navigate = useNavigate();
  const { refresh } = usePlaylists();
  const makePlaylist = async () => {
    setMaking(true);
    try {
      const { data } = await api.post("/ratings/playlist");
      refresh();
      toast.success(data.created ? "Created “My thumbs-up” — it refreshes itself as you rate songs" : "Opening your “My thumbs-up” playlist");
      navigate(`/playlists/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setMaking(false); }
  };
  const key = [...ids].sort().join(",");
  useEffect(() => {
    let alive = true;
    api.get("/ratings/tracks", { params: { rating } }).then((r) => alive && setTracks(r.data.items)).catch(() => alive && setTracks([]));
    return () => { alive = false; };
  }, [rating, key]);
  const up = rating === 1;
  const testid = up ? "liked" : "disliked";
  if (!tracks) return <Loader2 className="animate-spin text-zinc-400" />;
  const shown = tracks.filter((t) => ids.has(t.id));
  if (!shown.length) {
    return up
      ? <EmptyState icon={ThumbsUp} title="No thumbs-up songs yet" text="Give a song a thumbs-up and dynamic playlists will lean into it." />
      : <EmptyState icon={ThumbsDown} title="Nothing disliked" text="Songs you thumbs-down are kept out of dynamic playlists and AI mixes. They'll be listed here so you can undo." />;
  }
  return (
    <div data-testid={`${testid}-list`}>
      <div className="mb-6 flex flex-wrap items-center gap-3 text-xs text-zinc-500">
        {up ? (
          <>
            <span data-testid="liked-note">Dynamic playlists and AI mixes favour these.</span>
            <button onClick={() => p.playTracks(shown)} data-testid="liked-play" className="flex h-8 items-center gap-1.5 rounded-full bg-zinc-900 px-3 text-xs font-medium text-white"><Play size={11} fill="currentColor" />Play</button>
            <button onClick={() => p.playTracks([...shown].sort(() => Math.random() - 0.5))} data-testid="liked-shuffle" className="flex h-8 items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-3 text-xs"><Shuffle size={11} />Shuffle</button>
            <button onClick={makePlaylist} disabled={making} data-testid="liked-make-playlist" title="A dynamic playlist that updates itself as you rate songs"
              className="flex h-8 items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 text-xs text-amber-900 hover:border-amber-600 disabled:opacity-50">
              {making ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}“My thumbs-up” playlist
            </button>
          </>
        ) : (
          <>
            <span data-testid="disliked-note">Hidden from dynamic playlists and AI mixes. Tap the thumbs-down again on a row to undo.</span>
            <button onClick={() => shown.forEach((t) => rateTrack(t.id, 0))} data-testid="disliked-undo-all" className="flex h-8 items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-3 text-xs hover:border-zinc-900"><Undo2 size={11} />Undo all</button>
          </>
        )}
      </div>
      <TrackTable tracks={shown} showCover selectable removable={false} />
    </div>
  );
};
